import { type AxiosError } from "axios";
import apiInstance from "./instance";
import endpoints from "./endpoints";

export type AnalysisApiError = AxiosError<{
  status_message?: string;
  message?: string;
  [key: string]: unknown;
}>;

export interface Runner {
  runner_id: string;
  runner_name: string;
  profit: number;
}

export interface Market {
  _id: string;
  market_id: string;
  provider_market_id: string;
  event_id: string;
  runners: Runner[];
  member_code: string;
  bet_amount: number;
  market_name: string;
  market_type: string;
  event_name: string;
  amount: number;
  provider_status: string;
  provider_game_id: string;
}

export interface SportAnalysisEvent {
  event_id: string;
  event_name: string;
  provider_status: string;
  provider_game_id: string;
  totalAmount: number;
  matchOddMarkets: Market[];
  bookMakerMarkets: Market[];
  fancyMarkets: Market[];
  total_unsettle_bets: number;
}

export interface SportAnalysisResponse {
  data: SportAnalysisEvent[];
  status_code: number;
  status_message: string;
  response_error: boolean;
}

export interface CompetitionGroup {
  competitionName: string;
  events: SportAnalysisEvent[];
}

export interface EventDisplayData {
  eventId: string;
  eventName: string;
  eventDate?: string;
  totalBets: number;
  exposure: number;
  totalAmount: number;
  maxProfit: number;
}

// API Response interfaces for grouped-unsettle-bets endpoint
export interface GroupedUnsettleBetsMarket {
  market_name: string;
  total_bets: number;
  exposure: number;
  max_profit: number;
}

export interface GroupedUnsettleBetsEvent {
  event_id: string;
  event_name: string;
  event_time: string;
  total_bets: number;
  exposure: number;
  max_profit: number;
  total_amount: number;
  markets: GroupedUnsettleBetsMarket[];
}

export interface FlatUnsettleBetsEvent {
  event_id: string;
  event_name: string;
  total_bets: number;
  exposure: number;
  max_profit: number;
  total_amount: number;
  markets: GroupedUnsettleBetsMarket[];

  league_name: string;
  game_id: string;
  game_name: string;
  event_time: string;
}

export interface GroupedUnsettleBetsLeague {
  league_name: string;
  events: GroupedUnsettleBetsEvent[];
}

export interface GroupedUnsettleBetsGame {
  game_id: string;
  game_name: string;
  leagues: GroupedUnsettleBetsLeague[];
}

export interface GroupedUnsettleBetsResponse {
  data: GroupedUnsettleBetsGame[];
  status_code: number;
  status_message: string;
  response_error: boolean;
}

// API Request/Response interfaces for master-user-pl endpoint
export interface MasterUserPlRequest {
  marketId: string;
  role: string;
}

export interface MasterUserPlRunner {
  runner_id: string;
  runner_name: string;
  profit: number;
  exposure: number;
  total_book: number;
}

export interface MasterUserPlUserData {
  id: string;
  type: string;
  user_name: string;
  role_name: string;
  user_id?: string;
  runners: MasterUserPlRunner[];
  children: MasterUserPlUserData[];
}

export interface MasterUserPlResponse {
  data: MasterUserPlUserData[];
  status_code: number;
  status_message: string;
  response_error: boolean;
}

// API Response interfaces for market-settlement-details endpoint
export interface MarketSettlementDetails {
  market_id: string;
  provider_market_id: string;
  market_name: string;
  declare_date_time: string;
  total_winner: number;
  total_order_count: number;
  total_amount: number;
  total_profit_loss: number;
}

export interface MarketSettlementDetailsResponse {
  data: MarketSettlementDetails | MarketSettlementDetails[];
  status_code: number;
  status_message: string;
  response_error: boolean;
}

// API Request/Response interfaces for agent-bet endpoint
export interface AgentBetParams {
  from_date?: string | number;
  to_date?: string | number;
  game_name?: string;
  event?: string;
  event_id?: string;
  event_name?: string;
  market_type?: string;
  market?: string;
  market_id?: string;
  provider_market_id?: string;
  offset?: number;
  limit?: number;
  sort_column?: string;
  sort_order?: "asc" | "desc";
}

export interface AgentBetItem {
  bet_id?: string;
  bet_id_ui?: string;
  user_id?: string;
  user_name?: string;
  agent_id?: string;
  agent_name?: string;
  member_code?: string;
  event_id?: string;
  event_name?: string;
  market_name?: string;
  market_type?: string;
  provider_market_id?: string;
  market_id?: string;
  type?: "back" | "lay";
  odds?: number;
  bet_amount?: number;
  profit?: number;
  liability?: number;
  bet_type?: string;
  bet_status?: string;
  placed?: string;
  placed_at?: string;
  created_at?: string;
  updated_at?: string;
  provider_game_id?: string;
  matched_at?: string;
  ip_address?: string;
  game_name?: string;
  runner_name?: string;
  runner_id?: string;
  in_play?: string;
  is_fancy?: string;
  fromOneClick?: string;
  matched_at_odds?: string;
  requested_odds?: string;
  league_id?: string;
  league_name?: string;
  line?: string;
  remark?: string;
  is_win?: boolean;
  outcome?: "won" | "loss" | "voided" | string;
  voided_at?: string;
  [key: string]: unknown;
}

export interface AgentBetResponse {
  data: {
    count: number;
    listBet: AgentBetItem[];
  };
  status_code: number;
  status_message: string;
  response_error: boolean;
}

export const analysisApi = {
  async fetchSportAnalysis(): Promise<SportAnalysisResponse> {
    const { data } = await apiInstance.get<SportAnalysisResponse>(endpoints.analysis.sportAnalysis);
    return data;
  },
  async fetchGroupedUnsettleBets(): Promise<GroupedUnsettleBetsResponse> {
    const { data } = await apiInstance.get<GroupedUnsettleBetsResponse>(endpoints.analysis.groupedUnsettleBets);
    return data;
  },
  async fetchMasterUserPl(request: MasterUserPlRequest): Promise<MasterUserPlResponse> {
    const { data } = await apiInstance.post<MasterUserPlResponse>(endpoints.bet.masterUserPl, request);
    return data;
  },
  async fetchMarketSettlementDetails(eventId: string): Promise<MarketSettlementDetailsResponse> {
    const { data } = await apiInstance.get<MarketSettlementDetailsResponse>(endpoints.reports.marketSettlementDetails, {
      params: { event_id: eventId },
    });
    return data;
  },
  async fetchAgentBetList(params: AgentBetParams): Promise<AgentBetResponse> {
    const { data } = await apiInstance.get<AgentBetResponse>(endpoints.bet.agentBet, {
      params,
    });
    return data;
  },
  async voidAgentBet(
    betId: string
  ): Promise<{ data?: { bet_id: string }; status_code: number; status_message: string; response_error: boolean }> {
    const { data } = await apiInstance.put<{
      data?: { bet_id: string };
      status_code: number;
      status_message: string;
      response_error: boolean;
    }>(`${endpoints.bet.agentBet}/void`, { bet_id: betId });
    return data;
  },
};

export interface GetEventDetail {
  eventId: string;
  is_fancy: boolean;
}
export interface GetRaceEventDetail {
  event_id: string;
  market_id: string;
}

export interface PlaceBetPayload {
  agent_id: string;
  event_id: string;
  provider_market_id: string;
  market_type: string;
  runner_id: string;
  runner_name: string;
  type: "back" | "lay";
  odds: number;
  bet_amount: number;
  line: number;
  fromOneClick: number;
  is_fancy: boolean;
  is_fractional_odds: boolean;
  totalSize: number;
  ip_address: string;
}

export interface PlaceBetResponse {
  data?: unknown;
  status_code: number;
  status_message: string;
  response_error: boolean;
}

export const marketService = {
  getMarketById: ({ eventId, is_fancy }: GetEventDetail) =>
    apiInstance.get(endpoints.MARKET_BY_EVENT.replace(":eventId", eventId), {
      params: { is_fancy },
    }),
  getRaceMarketById: (params: GetRaceEventDetail) => apiInstance.get(endpoints.MARKET_BY_RACE, { params }),
  getAdminNetExposure: async (member_id: string) => {
    const response = await apiInstance.get(`/exposure/admin-net-exposure-new?member_id=${member_id}`);
    return response.data;
  },
  placeBet: async (payload: PlaceBetPayload): Promise<PlaceBetResponse> => {
    const { data } = await apiInstance.post<PlaceBetResponse>(endpoints.bet.place, payload);
    if (data.response_error) {
      throw new Error(data.status_message || "Failed to place bet");
    }
    return data;
  },
};
